/* eslint-disable max-lines */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import {
  CANCEL_TEXT,
  ENABLE_DISABLE_ZOHO_CUSTOMER_DIALOG,
} from '../../../Constants/strings';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import {
  createZohoClient,
  updateZohoClient,
  zohoClientDetailsList,
} from '../../../Api/request/zohoClientDetails';
import FormDialog from '../../../Components/Modal/modal';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import { ReactComponent as ErrorInfoIcon } from '../../../Assets/images/svg/errinfo.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { zohoClientDetailsTableConfig } from './zohoClientDetailsTableConfig';
import './zohoClientDetails.scss';

const INITIAL_CLIENT_FORM = {
  clientName: '',
  clientStatus: '',
};

const formatExportDate = () => new Date()
  .toISOString()
  .split('T')[0];

const readPathValue = (obj, path) => {
  if (!obj || !path) return undefined;

  return String(path)
    .split('.')
    .reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), obj);
};

const getFirstValueByPaths = (obj, paths = []) => {
  for (const path of paths) {
    const value = readPathValue(obj, path);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return undefined;
};

const getZohoClientIdValue = (row) => getFirstValueByPaths(row, [
  'zohoClientId',
  'zoho_client_id',
  'zohoClientCode',
  'zoho_client_code',
]);

const getZohoCustomerRecordIdValue = (row) => getFirstValueByPaths(row, [
  'zohoCustomerId',
  'zoho_customer_id',
  'customerId',
  'customer_id',
  'id',
  'value',
]);

const getDuplicateZohoClientMessage = ({
  message,
  clientName,
  clients = [],
}) => {
  const normalizedName = clientName?.trim().toLowerCase();
  const matchedClient = clients.find(
    (client) => client?.clientName?.trim().toLowerCase() === normalizedName,
  );

  const resolvedZohoClientId = getZohoClientIdValue(matchedClient);
  const messageZohoClientId = typeof message === 'string'
    ? message.match(/ZC-[A-Za-z0-9]+/i)?.[0]
    : '';
  const zohoClientId = resolvedZohoClientId || messageZohoClientId;

  if (!zohoClientId) {
    return 'The Zoho client name already exists.';
  }

  return `The Zoho client name already exists with Zoho client id ${zohoClientId}`;
};

const renderClientModal = ({
  clientForm,
  dialogMode,
  handleSubmitClient,
  handleClientFormChange,
  handleCloseClientDialog,
  isStatusHelpOpen,
  duplicateClientMessage,
  isSubmitDisabled,
  fieldErrors,
  setIsStatusHelpOpen,
}) => createPortal(
  <div className="zoho-client-details-modal-backdrop">
    {duplicateClientMessage && (
      <div className="zoho-client-details-warning-toast">
        <div className="zoho-client-details-warning-toast__content">
          <ErrorInfoIcon className="zoho-client-details-warning-toast__icon" />
          <span className="zoho-client-details-warning-toast__text">
            {duplicateClientMessage}
          </span>
        </div>
      </div>
    )}
    <form className="zoho-client-details-modal" onSubmit={handleSubmitClient}>
      <div className="zoho-client-details-modal__header">
        {dialogMode === 'edit' ? 'Edit Zoho Client' : 'Add Zoho Client'}
      </div>
      <div className="zoho-client-details-modal__body">
        <div className="zoho-client-details-modal__field">
          <label
            htmlFor="zoho-client-name"
            className={fieldErrors?.clientName ? 'is-error' : ''}
          >
            Zoho Client Name<span className="required-asterisk">*</span>
          </label>
          <input
            id="zoho-client-name"
            name="clientName"
            type="text"
            placeholder="Enter Zoho Client Name"
            value={clientForm.clientName}
            onChange={handleClientFormChange}
            className={fieldErrors?.clientName ? 'is-error' : ''}
          />
        </div>
        <div className="zoho-client-details-modal__field">
          <label
            htmlFor="zoho-client-status"
            className={fieldErrors?.clientStatus ? 'is-error' : ''}
          >
            Zoho Client Status<span className="required-asterisk">*</span>
            <button
              type="button"
              className="zoho-client-details-modal__info"
              aria-label="Client status help"
              onClick={() => setIsStatusHelpOpen((isOpen) => !isOpen)}
            >
              i
            </button>
            {isStatusHelpOpen && (
              <span className="zoho-client-details-modal__help-message">
                <span>Select &quot;Active&quot; if the client is active.</span>
                <span>Select &quot;Inactive&quot; if the client is inactive.</span>
              </span>
            )}
          </label>
          <select
            id="zoho-client-status"
            name="clientStatus"
            className={[
              clientForm.clientStatus === '' ? 'is-placeholder' : '',
              fieldErrors?.clientStatus ? 'is-error' : '',
            ].filter(Boolean).join(' ')}
            value={clientForm.clientStatus}
            onChange={handleClientFormChange}
          >
            <option value="">Select</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>
      <div className="zoho-client-details-modal__footer">
        <button
          type="button"
          className="zoho-client-details-modal__btn zoho-client-details-modal__btn--ghost"
          onClick={handleCloseClientDialog}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="zoho-client-details-modal__btn zoho-client-details-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {dialogMode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const getAxiosResultMessage = (result) => (
  result?.data?.response?.data?.message
  || result?.data?.response?.data?.externalMessage
  || result?.data?.message
  || ''
);

const getZohoClientPermissions = (permissions = {}) => (
  permissions[MODULE_NAMES.FINANCE_ZOHO_CUSTOMER_DETAILS]
  || permissions[MODULE_NAMES.FINANCE_MILESTONE]
  || []
);

const useZohoClientDetailsData = ({ hasAccess }) => {
  const [clients, setClients] = useState([]);
  const [__isLoading, setIsLoading] = useState(false);

  const loadClients = useCallback(async () => {
    if (!hasAccess('read')) {
      setClients([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await UseAxios(zohoClientDetailsList(), setIsLoading);
      const rows = response?.data?.data?.tableData || response?.data?.zohoClientDetails || [];
      setClients(rows.map((row) => ({
        ...row,
        id: getZohoCustomerRecordIdValue(row) ?? getZohoClientIdValue(row),
        zohoCustomerId: getZohoCustomerRecordIdValue(row) ?? getZohoClientIdValue(row),
        zohoClientId: getZohoClientIdValue(row) ?? getZohoCustomerRecordIdValue(row),
      })));
    } catch (_error) {
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  return {
    clients,
    __isLoading,
    setIsLoading,
    loadClients,
  };
};

const buildZohoClientsTableData = (clients) => (
  clients.map((client, index) => ({
    ...client,
    slNo: index + 1,
    clientStatusLabel: Number(client?.clientStatus) === 1 ? 'Active' : 'Inactive',
    action: (
      <div
        className="zoho-client-details-actions-cell"
        onClick={(event) => event.stopPropagation()}
      >
        {client.action}
      </div>
    ),
  }))
);

const buildZohoClientConfig = ({
  userProfile,
  showAddNew,
  total,
  onAddClick,
}) => ({
  generalSettings: {
    enableApiFiltering: false,
    showPagination: false,
    showToolBar: true,
    apiEndpoints: {
      download: {
        enabled: true,
        filename: () => `zoho-client-details-${formatExportDate()}`,
        sheetName: () => `zoho-client-details-${formatExportDate()}`,
        reportTitle: () => `Zoho Client Details - ${formatExportDate()}`,
        transformResponse: (rows) => ({
          tableConfiguration: [
            { field: 'slNo', header: 'SI.No', numeric: false },
            { field: 'zohoClientId', header: 'Zoho Client ID', numeric: false },
            { field: 'clientName', header: 'Zoho Client Name' },
            { field: 'clientStatusLabel', header: 'Zoho Client Status' },
          ],
          tableData: (rows || []).map((row, index) => ({
            slNo: index + 1,
            zohoClientId: String(getZohoClientIdValue(row) ?? ''),
            clientName: row?.clientName ?? '',
            clientStatusLabel: row?.clientStatusLabel ?? '',
            format: {
              slNo: '0',
              zohoClientId: '@',
            },
          })),
        }),
      },
    },
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || {},
      roleDetails: userProfile?.roleDetails || [],
      employeeId: userProfile?.employeeId || '',
      globalColors: userProfile?.siteConfiguration?.global_colors,
    },
    handlers: {
      handleAddIconClick: onAddClick,
    },
    toolBarSettings: {
      showAddNew,
      showFilterOption: false,
      showAppliedFilters: false,
      showDownloadExcel: true,
      showDownloadCsv: false,
      showExpandCollapse: false,
      showColumnCustomize: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: true,
      showToolBarHeading: true,
      toolBarHeading: 'Zoho Client Details',
      downloadExcelSheetName: 'Zoho Client Details',
    },
  },
  showToolBar: true,
  tableSettings: {
    size: 'small',
    enableTreeTable: false,
    showExpandCollapse: false,
    tableConfig: zohoClientDetailsTableConfig,
    loaderSkeleton: {
      rows: 10,
      bodyCells: 1,
      columns: zohoClientDetailsTableConfig.length,
    },
    paginationConfig: {
      page: 1,
      limit: Math.max(total || 0, 1000),
      total,
      rowsPerPageOptions: [5, 10, 25, 50],
    },
    tableScrollHeight: 'calc(100vh - 220px)',
    maxFrozenColumns: {
      desktop: 1,
      tablet: 1,
      mobile: 1,
    },
  },
});

const useZohoClientDialog = ({
  clients,
  setIsLoading,
  loadClients,
}) => {
  const [clientDialog, setClientDialog] = useState({
    mode: 'add',
    selectedZohoCustomerId: null,
    isOpen: false,
  });
  const [isStatusHelpOpen, setIsStatusHelpOpen] = useState(false);
  const [duplicateClientMessage, setDuplicateClientMessage] = useState('');
  const [clientForm, setClientForm] = useState(INITIAL_CLIENT_FORM);
  const [fieldErrors, setFieldErrors] = useState({
    clientName: false,
    clientStatus: false,
  });
  const duplicateToastTimerRef = useRef(null);

  useEffect(() => {
    if (!duplicateClientMessage) {
      return undefined;
    }

    duplicateToastTimerRef.current = window.setTimeout(() => {
      setDuplicateClientMessage('');
    }, 3000);

    return () => window.clearTimeout(duplicateToastTimerRef.current);
  }, [duplicateClientMessage]);

  const handleOpenAddClient = useCallback(() => {
    setClientForm(INITIAL_CLIENT_FORM);
    setIsStatusHelpOpen(false);
    setDuplicateClientMessage('');
    setFieldErrors({ clientName: false, clientStatus: false });
    setClientDialog({
      mode: 'add',
      selectedZohoCustomerId: null,
      isOpen: true,
    });
  }, []);

  const handleOpenEditClient = useCallback((client) => {
    setClientForm({
      clientName: client.clientName,
      clientStatus: String(client.clientStatus),
    });
    setIsStatusHelpOpen(false);
    setDuplicateClientMessage('');
    setFieldErrors({ clientName: false, clientStatus: false });
    setClientDialog({
      mode: 'edit',
      selectedZohoCustomerId: client.zohoCustomerId,
      isOpen: true,
    });
  }, []);

  const handleCloseClientDialog = useCallback(() => {
    setClientForm(INITIAL_CLIENT_FORM);
    setIsStatusHelpOpen(false);
    setDuplicateClientMessage('');
    setFieldErrors({ clientName: false, clientStatus: false });
    setClientDialog({
      mode: 'add',
      selectedZohoCustomerId: null,
      isOpen: false,
    });
  }, []);

  const handleClientFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setClientForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));

    setFieldErrors((currentErrors) => {
      if (!currentErrors[name]) return currentErrors;
      if (name === 'clientName' && value.trim()) return { ...currentErrors, clientName: false };
      if (name === 'clientStatus' && value !== '') return { ...currentErrors, clientStatus: false };
      return currentErrors;
    });
  }, []);

  const selectedClient = useMemo(() => clients.find((client) => (
    client.zohoCustomerId === clientDialog.selectedZohoCustomerId
  )), [clientDialog.selectedZohoCustomerId, clients]);

  const isSubmitDisabled = useMemo(() => {
    if (clientDialog.mode !== 'edit' || !selectedClient) {
      return false;
    }

    return (
      clientForm.clientName.trim() === selectedClient.clientName.trim()
      && String(clientForm.clientStatus) === String(selectedClient.clientStatus)
    );
  }, [clientDialog.mode, clientForm, selectedClient]);

  const handleSubmitClient = useCallback((event) => {
    event.preventDefault();
    const clientName = clientForm.clientName.trim();

    if (!clientName || clientForm.clientStatus === '') {
      setFieldErrors({
        clientName: !clientName,
        clientStatus: clientForm.clientStatus === '',
      });
      toast.error('Please enter client name and client status.');
      return;
    }

    if (clientDialog.mode === 'edit') {
      UseAxios(
        updateZohoClient(
          clientDialog.selectedZohoCustomerId,
          clientName,
          clientForm.clientStatus,
        ),
        setIsLoading,
      )
        .then((response) => {
          const responseMessage = getAxiosResultMessage(response);
          if (responseMessage) {
            setDuplicateClientMessage(getDuplicateZohoClientMessage({
              message: responseMessage,
              clientName,
              clients,
            }));
            return;
          }

          toast.success('Zoho client updated successfully.');
          handleCloseClientDialog();
          loadClients();
        });
      return;
    }

    UseAxios(
      createZohoClient(clientName, clientForm.clientStatus),
      setIsLoading,
    )
      .then((response) => {
        const responseMessage = getAxiosResultMessage(response);
        if (responseMessage) {
          setDuplicateClientMessage(getDuplicateZohoClientMessage({
            message: responseMessage,
            clientName,
            clients,
          }));
          return;
        }
        toast.success('Zoho client added successfully.');
        handleCloseClientDialog();
        loadClients();
      });
  }, [
    clientDialog.mode,
    clientDialog.selectedZohoCustomerId,
    clientForm.clientName,
    clientForm.clientStatus,
    handleCloseClientDialog,
    clients,
    loadClients,
    setIsLoading,
  ]);

  return {
    clientDialog,
    clientForm,
    isStatusHelpOpen,
    duplicateClientMessage,
    isSubmitDisabled,
    fieldErrors,
    handleOpenAddClient,
    handleOpenEditClient,
    handleCloseClientDialog,
    handleClientFormChange,
    handleSubmitClient,
    setIsStatusHelpOpen,
  };
};

const ZohoClientDetails = () => {
  const { userProfile } = useAuth();
  const permission = getZohoClientPermissions(userProfile?.permissions);
  const hasAccess = useCallback((action) => permission.includes(action), [permission]);

  const {
    clients,
    setIsLoading,
    loadClients,
  } = useZohoClientDetailsData({ hasAccess });

  const [toggleClient, setToggleClient] = useState(null);
  const toggleContent = useMemo(() => {
    const isCurrentlyActive = Boolean(toggleClient?.clientStatus);
    return isCurrentlyActive
      ? ENABLE_DISABLE_ZOHO_CUSTOMER_DIALOG.DISABLE
      : ENABLE_DISABLE_ZOHO_CUSTOMER_DIALOG.ENABLE;
  }, [toggleClient]);

  const {
    clientDialog,
    clientForm,
    isStatusHelpOpen,
    duplicateClientMessage,
    isSubmitDisabled,
    fieldErrors,
    handleOpenAddClient,
    handleOpenEditClient,
    handleCloseClientDialog,
    handleClientFormChange,
    handleSubmitClient,
    setIsStatusHelpOpen,
  } = useZohoClientDialog({
    clients,
    setIsLoading,
    loadClients,
  });

  const handleToggleClient = useCallback((client) => {
    setToggleClient(client);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleClient(null);
  }, []);

  const handleConfirmToggleClient = useCallback(async () => {
    if (!toggleClient) return;

    const nextStatus = Number(toggleClient.clientStatus) ? 0 : 1;
    const result = await UseAxios(
      updateZohoClient(
        toggleClient.zohoCustomerId,
        toggleClient.clientName,
        String(nextStatus),
      ),
      setIsLoading,
    );

    const message = getAxiosResultMessage(result);
    if (message) {
      toast.error(message);
      return;
    }

    toast.success(toggleContent.RESPONSE_SUCCESS);
    handleCloseToggleDialog();
    loadClients();
  }, [
    handleCloseToggleDialog,
    loadClients,
    setIsLoading,
    toggleClient,
    toggleContent.RESPONSE_SUCCESS,
  ]);

  const tableData = useMemo(
    () => buildZohoClientsTableData(
      clients.map((client) => ({
        ...client,
        action: (
          <div className="zoho-client-details-action-cell-inner">
            <ControlledSwitches
              checked={Boolean(client.clientStatus)}
              disabled={!hasAccess('update')}
              switchCss="switchCss"
              handleChange={() => handleToggleClient(client)}
            />
            <button
              type="button"
              className="zoho-client-details-action-btn"
              title="Edit Zoho Client"
              aria-label={`Edit ${client.clientName}`}
              disabled={!hasAccess('update')}
              onClick={() => handleOpenEditClient(client)}
            >
              <img src={EditActionIcon} alt="" />
            </button>
          </div>
        ),
      })),
    ),
    [clients, handleOpenEditClient, handleToggleClient, hasAccess],
  );

  const configuration = useMemo(
    () => buildZohoClientConfig({
      userProfile,
      showAddNew: hasAccess('create'),
      total: tableData.length,
      onAddClick: handleOpenAddClient,
    }),
    [handleOpenAddClient, hasAccess, tableData.length, userProfile],
  );

  if (!hasAccess('read')) {
    return <div>You do not have permission to view zoho client details.</div>;
  }

  return (
    <div className="zoho-client-details-tu">
      <TableUtility
        configuration={configuration}
        data={tableData}
      />
      {clientDialog.isOpen && renderClientModal({
        clientForm,
        dialogMode: clientDialog.mode,
        handleSubmitClient,
        handleClientFormChange,
        handleCloseClientDialog,
        isStatusHelpOpen,
        duplicateClientMessage,
        isSubmitDisabled,
        fieldErrors,
        setIsStatusHelpOpen,
      })}
      <FormDialog
        open={Boolean(toggleClient)}
        title={toggleContent.TITLE_TEXT}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">{toggleContent.MESSAGE_TEXT}</div>
          </div>
        )}
        button1={CANCEL_TEXT}
        cancelButtonId={`cancel-${toggleContent.BUTTON_ID}`}
        button2={toggleContent.BUTTON_TEXT}
        submitButtonId={toggleContent.BUTTON_ID}
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggleClient}
      />
    </div>
  );
};

export default ZohoClientDetails;
